import React from "react";
import { useAppSelector, useAppDispatch } from "../../../../../../../util/store-types";
import { format, parseISO } from "date-fns";
import { dateFnsFormats } from "rtr-constants";
import ProductImage from "components/source/shared/product-image";
import { toggleBag, bagTabs, switchBagTab } from "actions/bag-actions";

import { formatDateMonthDayCondensed } from "helpers/date-helpers";
import {
  getProductImageUrlsByOrientation,
  getProductImageAltText,
  ProductImageOrientation,
  ProductImageSize,
} from "helpers/product-image-helpers";

import RtrButton from "components/design-system/rtr-button";
import ICInfo from "./ic-info.svg";

const SwapConfirmation = () => {
  const dispatch = useAppDispatch();
  const swapOrderConfirmation = useAppSelector(state => state?.swapOrderConfirmation) || null;
  const returnByDate = useAppSelector(state => state?.membershipBag?.crossShipReturnByDateForToday) || ""; // will have been fetched already on the bag footer screen

  let arrivalDate = null;
  if (swapOrderConfirmation?.placedOrder?.checkout?.bagItems?.length) {
    // sort the bag items by their arrival date, descending
    const sortedBagItems = [...swapOrderConfirmation.placedOrder.checkout.bagItems].sort((a, b) => {
      if (!b.beginDate && !a.beginDate) return 0;
      if (!b.beginDate) return -1;
      if (!a.beginDate) return 1;

      return new Date(b.beginDate).getTime() - new Date(a.beginDate).getTime();
    });

    if (sortedBagItems[0] && sortedBagItems[0].beginDate) {
      arrivalDate = parseISO(sortedBagItems[0].beginDate);
    }
  }

  const numberOfReturningItems = swapOrderConfirmation?.returnDetails?.returningItems?.length || 0;

  const renderContent = () => {
    if (numberOfReturningItems) {
      return (
        <div className="px-4">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-lg">You&apos;re returning {numberOfReturningItems} items.</p>
            <a href="#" className="font-semibold underline">
              How To Return
            </a>
          </div>
          <div className="p-4 bg-lightMutedRed mb-6">
            <div className="text-lg font-semibold mb-2 flex items-center">
              <ICInfo className="mr-2" />
              <span>Return by {formatDateMonthDayCondensed(returnByDate)}</span>
            </div>
            <p>
              Please return your items on time to avoid late fees.{" "}
              <a href="#" className="font-semibold underline">
                Learn More
              </a>
            </p>
          </div>
          <div className="returning-list flex overflow-x-auto gap-2">
            {swapOrderConfirmation.returnDetails.returningItems.map((item, index) => (
              <div key={index} className="flex-none w-14">
                <ProductImage
                  className="rounded-md mb-2"
                  altText={getProductImageAltText(item.displayName, item.designerName)}
                  imageGroup={getProductImageUrlsByOrientation(item.images, [ProductImageOrientation.NO_MODEL.path])}
                  imageSize={ProductImageSize.x70}
                />

                <p className="text-sm text-darkerGray">Size {item.size}</p>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <p className="p-4 text-lg">Your next shipment is confirmed. We&apos;ll let you know when it&apos;s on the way.</p>
    );
  };

  const renderCta = () => {
    if (numberOfReturningItems) {
      return (
        <div className="px-4 py-8 bg-offWhite">
          <p className="text-lg font-semibold mb-2">No time to drop off returns?</p>
          <p className="mb-8 text-darkerGray">
            Schedule a home pickup. We&apos;ll return it for you, at no additional cost.
          </p>
          <RtrButton className="mt-4 w-full justify-self-end">Schedule Home Pickup</RtrButton>
        </div>
      );
    }

    return (
      <div className="px-4 py-8">
        <RtrButton
          className="mt-4 w-full justify-self-end"
          onClick={() => {
            dispatch(toggleBag(false));
            dispatch(switchBagTab(bagTabs.MEMBERSHIP_TAB));
          }}>
          Done
        </RtrButton>
      </div>
    );
  };

  return (
    <div className="swap-confirmation h-full flex flex-col justify-between">
      <div>
        {arrivalDate && (
          <p className="text-darkGray text-lg px-4 mb-6">Arriving: {format(arrivalDate, dateFnsFormats.MMM_D)}</p>
        )}
        {renderContent()}
      </div>
      {renderCta()}
    </div>
  );
};

export default SwapConfirmation;
